/* Audit Assistant — mock response bank (frontend-only demo).
 *
 * Static Q→A content used by MockChatProvider. NO backend. Keys are
 * matched case-insensitively with light fuzzy fallback (substring /
 * keyword). If nothing matches, DEFAULT_RESPONSE is returned so the
 * panel always answers.
 *
 * Every entry also carries `followUps` — the questions offered as chips
 * under that answer. They always point at other prompts in this bank, so
 * a demo can be clicked through indefinitely without dead ends.
 *
 * Formatting convention (rendered by ChatBubble): plain text with
 * newlines; `**bold**` for headers/values; lines starting with "• "
 * render as bullets. This content is fully controlled (never user
 * input), so bubble rendering builds React nodes — no HTML injection.
 */

export interface MockEntry {
  /** Canonical prompt label (also used as a suggestion chip). */
  prompt: string;
  /** Extra keywords that should resolve to this answer. */
  keywords: string[];
  answer: string;
  /** Suggested next questions, shown as chips under the answer. */
  followUps: string[];
}

/* Demo project context. The assistant shows a project selector; the
   chosen name is substituted into `{project}` placeholders in answers.
   Swap this list for real projects later without touching the UI. */
export const MOCK_PROJECTS: string[] = [
  'Project Aurora',
  'Project Delta',
  'Project Borealis',
  'Project Fjord',
];
export const DEFAULT_PROJECT = MOCK_PROJECTS[0];

export const DEFAULT_RESPONSE =
  "This is a demo version of **Audit Assistant**.\n\n" +
  "Backend AI integration is not connected yet.\n\n" +
  "This response is generated from mock data.";

/** Offered when nothing matched — steer the user back to answerable ground. */
export const DEFAULT_FOLLOWUPS: string[] = [
  'Summarize this project',
  'Explain Risk findings',
  'List pending conditions',
];

/* The 8 suggestion chips shown on the welcome screen, in display order. */
export const SUGGESTION_PROMPTS: string[] = [
  'Summarize this project',
  'Explain Risk findings',
  'Show DD observations',
  'Why is leverage flagged?',
  'Compare IRR',
  'Explain EBITDA',
  'List pending conditions',
  'Generate executive summary',
];

export const MOCK_ENTRIES: MockEntry[] = [
  {
    prompt: 'Summarize this project',
    keywords: ['summarize', 'summary of the project', 'project summary', 'overview'],
    answer:
      '{project} has successfully completed the Due Diligence review.\n\n' +
      '**Current Status**\n' +
      '• DD : Passed\n' +
      '• SPA : Conditional\n' +
      '• Risk : Conditional\n\n' +
      '**Key Observations**\n' +
      '• Revenue assumptions appear reasonable.\n' +
      '• EBITDA margin is within expected range.\n' +
      '• Leverage ratio requires further validation.\n' +
      '• Exit assumptions should be reviewed.\n\n' +
      '**Overall Recommendation**\n' +
      'Proceed with Conditions.',
    followUps: [
      'Why is leverage flagged?',
      'List pending conditions',
      'What are the exit assumptions?',
    ],
  },
  {
    prompt: 'Explain Risk findings',
    keywords: ['risk finding', 'risk observations', 'explain risk', 'risks'],
    answer:
      'Three major risks were identified.\n\n' +
      '• High leverage ratio\n' +
      '• Working capital sensitivity\n' +
      '• Vendor concentration\n\n' +
      '**Recommended Action**\n' +
      'Review financing assumptions before investment committee approval.',
    followUps: [
      'Why is leverage flagged?',
      'What mitigations are proposed?',
      'Who are the top customers?',
    ],
  },
  {
    prompt: 'Compare IRR',
    keywords: ['irr', 'compare irr', 'irr variance', 'internal rate of return'],
    answer:
      '**Base IRR**\n18.4%\n\n' +
      '**Sector Median**\n16.1%\n\n' +
      '**Variance**\n+2.3%\n\n' +
      'The project exceeds the benchmark IRR but depends on aggressive revenue growth assumptions.',
    followUps: [
      'What are the exit assumptions?',
      'Explain EBITDA',
      'Why is leverage flagged?',
    ],
  },
  {
    prompt: 'Show DD observations',
    keywords: ['dd observation', 'due diligence observation', 'dd findings', 'show dd'],
    answer:
      'The Due Diligence review surfaced the following observations.\n\n' +
      '**Financial**\n' +
      '• Revenue CAGR of 14% is supported by signed contracts.\n' +
      '• EBITDA margin of 22% is in line with sector peers.\n' +
      '• Net leverage of 4.1x sits above the 3.5x comfort threshold.\n\n' +
      '**Commercial**\n' +
      '• Top-5 customer concentration is 38% of revenue.\n' +
      '• Pipeline coverage for FY+1 is 1.8x.\n\n' +
      '**Recommendation**\n' +
      'Validate leverage headroom and customer concentration before close.',
    followUps: [
      'Who are the top customers?',
      'What is the covenant headroom?',
      'Explain EBITDA',
    ],
  },
  {
    prompt: 'Why is leverage flagged?',
    keywords: ['leverage', 'why is leverage', 'debt', 'gearing'],
    answer:
      'Leverage is flagged because net debt / EBITDA is **4.1x**, above the ' +
      '**3.5x** internal comfort threshold for this strategy.\n\n' +
      '**Drivers**\n' +
      '• Acquisition financing raised senior debt to 3.4x at close.\n' +
      '• A shareholder loan adds 0.7x of quasi-equity leverage.\n\n' +
      '**Why it matters**\n' +
      'Headroom to covenants is ~0.6x, which is sensitive to a working-capital swing.\n\n' +
      '**Recommended Action**\n' +
      'Stress-test the covenant headroom and confirm the amortisation profile.',
    followUps: [
      'What is the covenant headroom?',
      'What mitigations are proposed?',
      'List pending conditions',
    ],
  },
  {
    prompt: 'Explain EBITDA',
    keywords: ['ebitda', 'explain ebitda', 'margin'],
    answer:
      '**EBITDA** — Earnings Before Interest, Taxes, Depreciation & Amortisation — ' +
      'is a proxy for operating cash generation before capital structure and non-cash items.\n\n' +
      '**For this project**\n' +
      '• EBITDA margin: 22%\n' +
      '• Sector range: 18–24%\n' +
      '• Trend: stable over the last 3 years\n\n' +
      'The margin is within the expected range and supports the base-case valuation.',
    followUps: [
      'Compare IRR',
      'Why is leverage flagged?',
      'Show DD observations',
    ],
  },
  {
    prompt: 'List pending conditions',
    keywords: ['pending condition', 'conditions', 'list pending', 'outstanding'],
    answer:
      'The following conditions are outstanding.\n\n' +
      '• Confirm covenant headroom under the downside case.\n' +
      '• Obtain legal opinion on the anti-dilution clause.\n' +
      '• Verify the vendor concentration mitigation plan.\n' +
      '• Finalise the escrow amount for the indemnity cap.\n\n' +
      '**Status**\n' +
      'Proceed with Conditions — all items to be cleared before IC approval.',
    followUps: [
      'What is the covenant headroom?',
      'What mitigations are proposed?',
      'Generate executive summary',
    ],
  },
  {
    prompt: 'Generate executive summary',
    keywords: ['executive summary', 'exec summary', 'generate summary'],
    answer:
      '**Executive Summary — {project}**\n\n' +
      '{project} is a control buyout in the logistics sector. The Due Diligence review is ' +
      'complete (Passed); SPA and Risk are Conditional pending a short list of items.\n\n' +
      '**Financials**\n' +
      '• Base-case levered IRR: 18.4% (+2.3pt vs 16.1% sector median)\n' +
      '• EBITDA margin: 22% (in range)\n' +
      '• Net leverage: 4.1x (above 3.5x threshold)\n\n' +
      '**Risks**\n' +
      '• High leverage, working-capital sensitivity, vendor concentration.\n\n' +
      '**Recommendation**\n' +
      'Proceed with Conditions, subject to clearing the outstanding items before IC approval.',
    followUps: [
      'List pending conditions',
      'Explain Risk findings',
      'What are the exit assumptions?',
    ],
  },
  {
    prompt: 'What is the covenant headroom?',
    keywords: ['covenant', 'headroom', 'breach', 'amortisation'],
    answer:
      'Headroom to the leverage covenant is **~0.6x** of EBITDA.\n\n' +
      '**Covenant Set**\n' +
      '• Net leverage: 4.7x cap vs 4.1x actual (0.6x headroom)\n' +
      '• Interest cover: 2.5x floor vs 3.1x actual (0.6x headroom)\n\n' +
      '**Downside Case**\n' +
      'A 10% EBITDA decline pushes net leverage to 4.55x — inside the cap, but with ' +
      'only 0.15x of headroom left.\n\n' +
      '**Recommended Action**\n' +
      'Request an equity cure right, or negotiate the cap up to 5.0x before signing.',
    followUps: [
      'Why is leverage flagged?',
      'What mitigations are proposed?',
      'List pending conditions',
    ],
  },
  {
    prompt: 'Who are the top customers?',
    keywords: ['top customer', 'customer concentration', 'vendor concentration', 'concentration'],
    answer:
      'Top-5 customers account for **38%** of revenue — above the 30% concentration threshold.\n\n' +
      '**Breakdown**\n' +
      '• Customer A — 14% of revenue, contract to FY+3\n' +
      '• Customer B — 9%, contract to FY+2\n' +
      '• Customer C — 7%, rolling 12-month terms\n' +
      '• Customer D — 5%, contract to FY+2\n' +
      '• Customer E — 3%, rolling 12-month terms\n\n' +
      '**Why it matters**\n' +
      'Customers C and E are on rolling terms, so ~10% of revenue can be re-priced or ' +
      'lost within a year.\n\n' +
      '**Recommended Action**\n' +
      'Confirm renewal intent for the rolling accounts before close.',
    followUps: [
      'What mitigations are proposed?',
      'Show DD observations',
      'Explain Risk findings',
    ],
  },
  {
    prompt: 'What mitigations are proposed?',
    keywords: ['mitigation', 'mitigations', 'remediation', 'how to mitigate'],
    answer:
      'Mitigations proposed against the three flagged risks.\n\n' +
      '**High leverage**\n' +
      '• Negotiate the leverage cap up to 5.0x, plus an equity cure right.\n' +
      '• Confirm a back-ended amortisation profile.\n\n' +
      '**Working capital sensitivity**\n' +
      '• Size the revolver at 1.5x the peak intra-year swing.\n\n' +
      '**Vendor concentration**\n' +
      '• Secure multi-year renewals for the rolling accounts.\n' +
      '• Add an earn-out clawback tied to top-5 revenue retention.\n\n' +
      '**Status**\n' +
      'All three mitigations are open items on the pending-conditions list.',
    followUps: [
      'List pending conditions',
      'What is the covenant headroom?',
      'Who are the top customers?',
    ],
  },
  {
    prompt: 'What are the exit assumptions?',
    keywords: ['exit', 'exit assumption', 'exit multiple', 'hold period'],
    answer:
      'The base case assumes a **trade sale in year 5** at a **9.0x** EBITDA multiple.\n\n' +
      '**Assumptions**\n' +
      '• Entry multiple: 8.5x\n' +
      '• Exit multiple: 9.0x (+0.5x of multiple expansion)\n' +
      '• Hold period: 5 years\n\n' +
      '**Sensitivity**\n' +
      '• Exit at entry multiple (8.5x): IRR falls to 16.7%\n' +
      '• Exit at 8.0x: IRR falls to 14.9%, below the 16.1% sector median\n\n' +
      '**Why it matters**\n' +
      'The IRR premium over the benchmark relies on multiple expansion, which is not ' +
      'supported by a signed exit path.\n\n' +
      '**Recommended Action**\n' +
      'Re-run the base case at a flat entry/exit multiple.',
    followUps: [
      'Compare IRR',
      'Summarize this project',
      'Generate executive summary',
    ],
  },
];
